import React from "react";
import {
    SettingsBlockHeader,
    SettingsBlockTitle,
    BlockWrapper,
} from "pages/settings/settingsPageStyles";
import {
    SettingsName,
    SPSettingsValue,
    TickerItemDivForMDP,
} from "../CommonSettingsBlock/CommonSettingsBlockStyles";
import { UnderlinedInput } from "components/Inputs/inputsStyles";
import Loader from "components/Loader/Loader";
import { useAppSelector } from "hooks/redux";
import { BasedSelect } from "components/Selects/BasedSelect";
import { Select } from "antd";
import { ConfigFileDetails } from "store/reducers/SettingsSlice";

type Props = {
    rows: { row: string[]; originalIndex: number }[];
    selectedRowIndex: number | null;
    params: string[];
    onFieldChange: (idx: number, value: string) => void;
    MDPParamTypes: Record<string, string>;
    configMetadata?: ConfigFileDetails;
};

const MDPParamsBlock = ({
    rows,
    selectedRowIndex,
    params,
    onFieldChange,
    MDPParamTypes,
    configMetadata,
}: Props) => {
    const selectedRow =
        selectedRowIndex === null
            ? null
            : rows.find((item) => item.originalIndex === selectedRowIndex)
                  ?.row || null;
    const { loading, MDPTypes, mdpSources } = useAppSelector((s) => s.settings);
    const stsSchedules = useAppSelector((state) => state.settings.stsSchedules);
    const availableSCIds = useAppSelector(
        (state) => state.settings.availableSCIds,
    );
    const { Option } = Select;
    const shouldShowField = (idx: number): boolean => {
        if (!selectedRow) return false;
        if (typeof params[idx] !== "string" || params[idx].length === 0) {
            return false;
        }
        // null означает, что поле не разрешено для Source.Type этой строки (см. MdpSourceTypeField)
        return selectedRow[idx] !== null && selectedRow[idx] !== undefined;
    };
    const handleChange = (idx: number, value: string, type?: string) => {
        if (!type || type === "String") return onFieldChange(idx, value);

        if (type === "Integer") {
            const cleaned = value.replace(/[^0-9-]/g, "");
            onFieldChange(
                idx,
                value.startsWith("-") && cleaned === "" ? "-" : cleaned,
            );
            return;
        }

        if (type === "Float") {
            let cleaned = value.replace(/[^0-9.-]/g, "");
            const parts = cleaned.split(".");
            if (parts.length > 2)
                cleaned = parts[0] + "." + parts.slice(1).join("");
            if (cleaned.includes("-") && !cleaned.startsWith("-"))
                cleaned = cleaned.replace(/-/g, "");
            onFieldChange(idx, cleaned);
            return;
        }

        if (type === "Boolean") {
            const lower = value.toLowerCase();
            if (lower === "true" || lower === "1") onFieldChange(idx, "1");
            else if (lower === "false" || lower === "0")
                onFieldChange(idx, "0");
            else if (value === "") onFieldChange(idx, "");
        }
    };
    const getPlaceholder = (type?: string) => {
        switch (type) {
            case "Integer":
                return "целое число";
            case "Float":
                return "число";
            case "Boolean":
                return "0 или 1";
            case "String":
                return "текст";
            default:
                return "Введите значение";
        }
    };
    const renderValueInput = (
        param: string,
        idx: number,
        value: string,
        type: string,
    ) => {
        const fieldMeta = configMetadata?.fields?.find((f) => f.name === param);
        const isFieldEditable = fieldMeta?.is_editable;
        const isDisabled =
            configMetadata?.readonly || isFieldEditable === false;

        if (param === "ID.source") {
            return (
                <BasedSelect
                    key={`idSource-${idx}-${value}`}
                    size="small"
                    value={value || ""}
                    onChange={(newValue) => onFieldChange(idx, newValue)}
                    disabled={isDisabled}
                    style={{ width: "100%" }}
                >
                    {mdpSources?.map((source) => (
                        <Option key={source} value={source}>
                            {source}
                        </Option>
                    )) || <Option disabled>Нет источников</Option>}
                </BasedSelect>
            );
        }
        if (param === "SheduleID") {
            return (
                <BasedSelect
                    value={value || ""}
                    onChange={(newValue) => onFieldChange(idx, newValue)}
                    disabled={isDisabled}
                    style={{ width: "100%" }}
                    dropdownStyle={{ zIndex: 1050 }}
                >
                    {(availableSCIds || []).map((id: string) => (
                        <Option key={id} value={id}>
                            {id}
                        </Option>
                    ))}
                </BasedSelect>
            );
        }
        if (param === "Source.Type") {
            return (
                <BasedSelect
                    key={`sourceType-${idx}-${value}`}
                    size="small"
                    value={value || ""}
                    onChange={(newValue) => onFieldChange(idx, newValue)}
                    disabled={isDisabled}
                    style={{ width: "100%" }}
                >
                    {MDPTypes.map((t) => (
                        <Option key={t} value={t}>
                            {t}
                        </Option>
                    ))}
                </BasedSelect>
            );
        }
        if (param === "Source.type.shedule.ID") {
            return (
                <BasedSelect
                    key={value}
                    size="small"
                    value={value || ""}
                    onChange={(newValue) => onFieldChange(idx, newValue)}
                    disabled={isDisabled}
                    style={{ width: "100%" }}
                >
                    {stsSchedules.map((schedule) => (
                        <Option key={schedule.id} value={schedule.id}>
                            {schedule.id}: {schedule.start_time} -{" "}
                            {schedule.end_time}
                        </Option>
                    ))}
                </BasedSelect>
            );
        }

        return (
            <UnderlinedInput
                type="text"
                value={value}
                onChange={(e) => handleChange(idx, e.target.value, type)}
                disabled={isDisabled}
                placeholder={getPlaceholder(type)}
                style={{
                    cursor: isDisabled ? "not-allowed" : "text",
                    borderColor:
                        (type === "Integer" &&
                            value &&
                            !/^-?\d*$/.test(value)) ||
                        (type === "Float" && value && isNaN(Number(value))) ||
                        (type === "Boolean" &&
                            value &&
                            !["0", "1"].includes(value))
                            ? "#ff4d4f"
                            : undefined,
                }}
            />
        );
    };
    return (
        <>
            <SettingsBlockHeader>
                <SettingsBlockTitle>Параметры MDP</SettingsBlockTitle>
            </SettingsBlockHeader>
            <BlockWrapper>
                {loading && <Loader size="large" />}
                {selectedRow && (
                    <>
                        {params.map((param, idx) => {
                            if (!shouldShowField(idx)) return null;
                            const value = selectedRow[idx] ?? "";
                            const type =
                                configMetadata?.fields?.find(
                                    (field) => field.name === param,
                                )?.field_type || MDPParamTypes[param];

                            return (
                                <TickerItemDivForMDP key={param}>
                                    <SettingsName>{param}</SettingsName>
                                    <SPSettingsValue>
                                        {renderValueInput(
                                            param,
                                            idx,
                                            value,
                                            type,
                                        )}
                                    </SPSettingsValue>
                                </TickerItemDivForMDP>
                            );
                        })}
                    </>
                )}
            </BlockWrapper>
        </>
    );
};

export default MDPParamsBlock;
